// Assessment questionnaire templates for various screenings
// Each assessment has questions, scoring logic, and interpretation

export const assessmentTemplates = {
  // Social Determinants of Health (SDOH) Screening
  sdoh: {
    name: "Social Determinants of Health (SDOH) Screening",
    description: "Assess social, economic, and environmental factors affecting health",
    questions: [
      {
        id: "housing",
        text: "What is your living situation today?",
        type: "radio",
        options: [
          { value: "stable", label: "I have a steady place to live", score: 0 },
          { value: "worried", label: "I am worried about losing my housing", score: 1 },
          { value: "no_housing", label: "I do not have a steady place to live", score: 2 }
        ]
      },
      {
        id: "food",
        text: "In the past 12 months, have you worried that your food would run out before you got money to buy more?",
        type: "radio",
        options: [
          { value: "never", label: "Never", score: 0 },
          { value: "sometimes", label: "Sometimes", score: 1 },
          { value: "often", label: "Often", score: 2 }
        ]
      },
      {
        id: "transportation",
        text: "In the past 12 months, has lack of reliable transportation kept you from medical appointments, meetings, work, or from getting things needed for daily living?",
        type: "radio",
        options: [
          { value: "no", label: "No", score: 0 },
          { value: "yes", label: "Yes", score: 2 }
        ]
      },
      {
        id: "utilities",
        text: "In the past 12 months, has the electric, gas, oil, or water company threatened to shut off services in your home?",
        type: "radio",
        options: [
          { value: "no", label: "No", score: 0 },
          { value: "yes", label: "Yes", score: 1 }
        ]
      },
      {
        id: "safety",
        text: "How often does anyone, including family and friends, physically hurt you?",
        type: "radio",
        options: [
          { value: "never", label: "Never", score: 0 },
          { value: "rarely", label: "Rarely", score: 1 },
          { value: "sometimes", label: "Sometimes", score: 2 },
          { value: "often", label: "Fairly often", score: 3 },
          { value: "frequently", label: "Frequently", score: 4 }
        ]
      }
    ],
    maxScore: 11,
    interpretation: (score) => {
      if (score === 0) return { level: "low", text: "No significant social determinants identified" };
      if (score <= 3) return { level: "medium", text: "Some social needs identified - consider community resources" };
      return { level: "high", text: "Multiple social needs identified - immediate intervention recommended" };
    }
  },

  // Fall Risk Assessment
  fall_risk: {
    name: "Fall Risk Assessment",
    description: "Evaluate patient's risk of falling",
    questions: [
      {
        id: "history",
        text: "Have you fallen in the past year?",
        type: "radio",
        options: [
          { value: "no", label: "No", score: 0 },
          { value: "once", label: "Once", score: 1 },
          { value: "multiple", label: "Two or more times", score: 2 }
        ]
      },
      {
        id: "medications",
        text: "Are you currently taking 4 or more prescription medications?",
        type: "radio",
        options: [
          { value: "no", label: "No", score: 0 },
          { value: "yes", label: "Yes", score: 1 }
        ]
      },
      {
        id: "assistive_device",
        text: "Do you use a cane or walker?",
        type: "radio",
        options: [
          { value: "no", label: "No", score: 0 },
          { value: "yes", label: "Yes", score: 1 }
        ]
      },
      {
        id: "balance",
        text: "Do you have difficulty with balance or walking?",
        type: "radio",
        options: [
          { value: "no", label: "No", score: 0 },
          { value: "sometimes", label: "Sometimes", score: 1 },
          { value: "often", label: "Often", score: 2 }
        ]
      },
      {
        id: "dizziness",
        text: "Do you experience dizziness or lightheadedness when standing?",
        type: "radio",
        options: [
          { value: "no", label: "No", score: 0 },
          { value: "sometimes", label: "Sometimes", score: 1 },
          { value: "often", label: "Often", score: 2 }
        ]
      }
    ],
    maxScore: 8,
    interpretation: (score) => {
      if (score <= 2) return { level: "low", text: "Low fall risk - Continue regular safety practices" };
      if (score <= 4) return { level: "medium", text: "Moderate fall risk - Home safety evaluation recommended" };
      return { level: "high", text: "High fall risk - Immediate fall prevention interventions needed" };
    }
  },

  // Home Safety Assessment
  home_safety: {
    name: "Home Safety Assessment",
    description: "Evaluate home environment safety",
    questions: [
      {
        id: "lighting",
        text: "Are all areas of your home well-lit?",
        type: "radio",
        options: [
          { value: "yes", label: "Yes", score: 0 },
          { value: "no", label: "No", score: 1 }
        ]
      },
      {
        id: "rugs",
        text: "Do you have loose rugs or clutter on floors?",
        type: "radio",
        options: [
          { value: "no", label: "No", score: 0 },
          { value: "yes", label: "Yes", score: 1 }
        ]
      },
      {
        id: "grab_bars",
        text: "Do you have grab bars in the bathroom?",
        type: "radio",
        options: [
          { value: "yes", label: "Yes", score: 0 },
          { value: "no", label: "No", score: 1 }
        ]
      },
      {
        id: "smoke_detectors",
        text: "Do you have working smoke detectors?",
        type: "radio",
        options: [
          { value: "yes", label: "Yes", score: 0 },
          { value: "no", label: "No", score: 2 }
        ]
      },
      {
        id: "emergency_access",
        text: "Can emergency services easily access your home?",
        type: "radio",
        options: [
          { value: "yes", label: "Yes", score: 0 },
          { value: "no", label: "No", score: 1 }
        ]
      }
    ],
    maxScore: 6,
    interpretation: (score) => {
      if (score === 0) return { level: "low", text: "Home environment is safe" };
      if (score <= 2) return { level: "medium", text: "Minor safety improvements recommended" };
      return { level: "high", text: "Significant safety hazards identified - home modifications needed" };
    }
  },

  // Nutrition Screening
  nutrition: {
    name: "Nutrition Risk Screening",
    description: "Assess nutritional status and food security",
    questions: [
      {
        id: "weight_loss",
        text: "Have you unintentionally lost weight in the past 3 months?",
        type: "radio",
        options: [
          { value: "no", label: "No", score: 0 },
          { value: "under5", label: "Yes, less than 5 pounds", score: 1 },
          { value: "over5", label: "Yes, 5-10 pounds", score: 2 },
          { value: "over10", label: "Yes, more than 10 pounds", score: 3 }
        ]
      },
      {
        id: "appetite",
        text: "How would you describe your appetite?",
        type: "radio",
        options: [
          { value: "good", label: "Good", score: 0 },
          { value: "fair", label: "Fair", score: 1 },
          { value: "poor", label: "Poor", score: 2 }
        ]
      },
      {
        id: "meals",
        text: "How many meals do you eat per day?",
        type: "radio",
        options: [
          { value: "three_plus", label: "3 or more", score: 0 },
          { value: "two", label: "2", score: 1 },
          { value: "one_or_less", label: "1 or less", score: 2 }
        ]
      },
      {
        id: "difficulty_eating",
        text: "Do you have difficulty chewing or swallowing?",
        type: "radio",
        options: [
          { value: "no", label: "No", score: 0 },
          { value: "yes", label: "Yes", score: 1 }
        ]
      },
      {
        id: "food_access",
        text: "Do you have enough money to buy the food you need?",
        type: "radio",
        options: [
          { value: "yes", label: "Yes", score: 0 },
          { value: "sometimes", label: "Sometimes", score: 1 },
          { value: "no", label: "No", score: 2 }
        ]
      }
    ],
    maxScore: 10,
    interpretation: (score) => {
      if (score <= 2) return { level: "low", text: "Low nutritional risk" };
      if (score <= 5) return { level: "medium", text: "Moderate nutritional risk - dietary counseling recommended" };
      return { level: "high", text: "High nutritional risk - dietitian referral recommended" };
    }
  },

  // PHQ-9 Depression Screening
  phq9: {
    name: "PHQ-9 Depression Screening",
    description: "Patient Health Questionnaire for depression",
    instructions: "Over the last 2 weeks, how often have you been bothered by any of the following problems?",
    questions: [
      {
        id: "interest",
        text: "Little interest or pleasure in doing things",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      },
      {
        id: "depressed",
        text: "Feeling down, depressed, or hopeless",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      },
      {
        id: "sleep",
        text: "Trouble falling or staying asleep, or sleeping too much",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      },
      {
        id: "energy",
        text: "Feeling tired or having little energy",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      },
      {
        id: "appetite",
        text: "Poor appetite or overeating",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      },
      {
        id: "self_worth",
        text: "Feeling bad about yourself - or that you are a failure or have let yourself or your family down",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      },
      {
        id: "concentration",
        text: "Trouble concentrating on things, such as reading the newspaper or watching television",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      },
      {
        id: "movement",
        text: "Moving or speaking so slowly that other people could have noticed. Or being so fidgety or restless that you have been moving around a lot more than usual",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      },
      {
        id: "self_harm",
        text: "Thoughts that you would be better off dead, or of hurting yourself",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      }
    ],
    maxScore: 27,
    interpretation: (score) => {
      if (score <= 4) return { level: "low", text: "Minimal depression" };
      if (score <= 9) return { level: "medium", text: "Mild depression - watchful waiting" };
      if (score <= 14) return { level: "medium", text: "Moderate depression - consider treatment" };
      if (score <= 19) return { level: "high", text: "Moderately severe depression - active treatment warranted" };
      return { level: "critical", text: "Severe depression - active treatment strongly recommended" };
    }
  },

  // GAD-7 (Generalized Anxiety Disorder 7-item scale)
  gad7: {
    name: "GAD-7 Anxiety Assessment",
    description: "Screen for generalized anxiety disorder (7 standardized questions)",
    questions: [
      {
        id: "nervous",
        text: "Feeling nervous, anxious, or on edge",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      },
      {
        id: "worry_control",
        text: "Not being able to stop or control worrying",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      },
      {
        id: "excessive_worry",
        text: "Worrying too much about different things",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      },
      {
        id: "trouble_relaxing",
        text: "Trouble relaxing",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      },
      {
        id: "restless",
        text: "Being so restless that it is hard to sit still",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      },
      {
        id: "irritable",
        text: "Becoming easily annoyed or irritable",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      },
      {
        id: "afraid",
        text: "Feeling afraid, as if something awful might happen",
        type: "radio",
        options: [
          { value: "0", label: "Not at all", score: 0 },
          { value: "1", label: "Several days", score: 1 },
          { value: "2", label: "More than half the days", score: 2 },
          { value: "3", label: "Nearly every day", score: 3 }
        ]
      }
    ],
    maxScore: 21,
    interpretation: (score) => {
      if (score <= 4) return { level: "low", text: "Minimal anxiety" };
      if (score <= 9) return { level: "medium", text: "Mild anxiety - monitor symptoms" };
      if (score <= 14) return { level: "high", text: "Moderate anxiety - consider treatment" };
      return { level: "critical", text: "Severe anxiety - active treatment strongly recommended" };
    }
  },

  // ADL/IADL (Activities of Daily Living / Instrumental Activities)
  adl: {
    name: "ADL/IADL Functional Assessment",
    description: "Assess basic (Katz ADL) and instrumental (Lawton IADL) activities of daily living",
    questions: [
      // Katz ADL items (6)
      {
        id: "bathing",
        text: "Bathing: ability to bathe independently",
        type: "radio",
        section: "Basic ADL (Katz)",
        options: [
          { value: "1", label: "Independent — bathes self completely or needs help with one body part", score: 1 },
          { value: "0", label: "Dependent — needs help with more than one body part", score: 0 }
        ]
      },
      {
        id: "dressing",
        text: "Dressing: ability to get clothes and dress independently",
        type: "radio",
        section: "Basic ADL (Katz)",
        options: [
          { value: "1", label: "Independent — gets clothes, dresses without assistance", score: 1 },
          { value: "0", label: "Dependent — needs help dressing", score: 0 }
        ]
      },
      {
        id: "toileting",
        text: "Toileting: ability to use the toilet independently",
        type: "radio",
        section: "Basic ADL (Katz)",
        options: [
          { value: "1", label: "Independent — goes to toilet, uses it, cleans self", score: 1 },
          { value: "0", label: "Dependent — needs help with toileting", score: 0 }
        ]
      },
      {
        id: "transferring",
        text: "Transferring: ability to move in and out of bed/chair",
        type: "radio",
        section: "Basic ADL (Katz)",
        options: [
          { value: "1", label: "Independent — moves in/out of bed or chair without assistance", score: 1 },
          { value: "0", label: "Dependent — needs help transferring", score: 0 }
        ]
      },
      {
        id: "continence",
        text: "Continence: ability to control bladder and bowel",
        type: "radio",
        section: "Basic ADL (Katz)",
        options: [
          { value: "1", label: "Independent — complete self-control", score: 1 },
          { value: "0", label: "Dependent — partial or total incontinence", score: 0 }
        ]
      },
      {
        id: "feeding",
        text: "Feeding: ability to feed self",
        type: "radio",
        section: "Basic ADL (Katz)",
        options: [
          { value: "1", label: "Independent — feeds self without assistance", score: 1 },
          { value: "0", label: "Dependent — needs help feeding", score: 0 }
        ]
      },
      // Lawton IADL items (8)
      {
        id: "telephone",
        text: "Using the telephone",
        type: "radio",
        section: "Instrumental ADL (Lawton)",
        options: [
          { value: "1", label: "Uses telephone independently, looks up numbers", score: 1 },
          { value: "0", label: "Unable to use telephone or needs help", score: 0 }
        ]
      },
      {
        id: "shopping",
        text: "Shopping for groceries and necessities",
        type: "radio",
        section: "Instrumental ADL (Lawton)",
        options: [
          { value: "1", label: "Shops independently for all needs", score: 1 },
          { value: "0", label: "Needs accompaniment or is unable to shop", score: 0 }
        ]
      },
      {
        id: "food_prep",
        text: "Food preparation",
        type: "radio",
        section: "Instrumental ADL (Lawton)",
        options: [
          { value: "1", label: "Plans, prepares, and serves meals independently", score: 1 },
          { value: "0", label: "Needs help or is unable to prepare meals", score: 0 }
        ]
      },
      {
        id: "housekeeping",
        text: "Housekeeping",
        type: "radio",
        section: "Instrumental ADL (Lawton)",
        options: [
          { value: "1", label: "Maintains house alone or with occasional help", score: 1 },
          { value: "0", label: "Needs help with all housekeeping tasks", score: 0 }
        ]
      },
      {
        id: "laundry",
        text: "Laundry",
        type: "radio",
        section: "Instrumental ADL (Lawton)",
        options: [
          { value: "1", label: "Does personal laundry completely", score: 1 },
          { value: "0", label: "Needs help with laundry", score: 0 }
        ]
      },
      {
        id: "transportation",
        text: "Mode of transportation",
        type: "radio",
        section: "Instrumental ADL (Lawton)",
        options: [
          { value: "1", label: "Travels independently via public transit or drives", score: 1 },
          { value: "0", label: "Requires arranged transport or is unable to travel", score: 0 }
        ]
      },
      {
        id: "medications",
        text: "Responsibility for own medications",
        type: "radio",
        section: "Instrumental ADL (Lawton)",
        options: [
          { value: "1", label: "Takes medications on own, correct doses at correct times", score: 1 },
          { value: "0", label: "Needs reminders or someone to prepare medications", score: 0 }
        ]
      },
      {
        id: "finances",
        text: "Ability to handle finances",
        type: "radio",
        section: "Instrumental ADL (Lawton)",
        options: [
          { value: "1", label: "Manages finances independently (bills, banking, budgeting)", score: 1 },
          { value: "0", label: "Needs help managing finances", score: 0 }
        ]
      }
    ],
    maxScore: 14, // 6 Katz + 8 Lawton
    interpretation: (score) => {
      // Katz portion (items 0-5): 0-6 where 6=fully independent
      // Lawton portion (items 6-13): 0-8 where 8=fully independent
      if (score >= 12) return { level: "low", text: "Highly independent — minimal functional limitations" };
      if (score >= 8) return { level: "medium", text: "Mild functional limitations — may benefit from support services" };
      if (score >= 4) return { level: "high", text: "Moderate dependence — home health or caregiver support recommended" };
      return { level: "critical", text: "Severe dependence — intensive support or facility-level care needed" };
    }
  },

  // Health Risk Assessment (HRA)
  hra: {
    name: "Health Risk Assessment (HRA)",
    description: "Comprehensive annual health risk assessment covering functional status, cognitive, social support, and advance directives",
    questions: [
      {
        id: "self_rated_health",
        text: "In general, how would you rate your health?",
        type: "radio",
        section: "General Health",
        options: [
          { value: "excellent", label: "Excellent", score: 0 },
          { value: "very_good", label: "Very good", score: 0 },
          { value: "good", label: "Good", score: 1 },
          { value: "fair", label: "Fair", score: 2 },
          { value: "poor", label: "Poor", score: 3 }
        ]
      },
      {
        id: "health_change",
        text: "Compared to one year ago, how would you rate your health now?",
        type: "radio",
        section: "General Health",
        options: [
          { value: "better", label: "Much better or somewhat better", score: 0 },
          { value: "same", label: "About the same", score: 1 },
          { value: "worse", label: "Somewhat worse or much worse", score: 2 }
        ]
      },
      {
        id: "er_visits",
        text: "In the past 6 months, how many times have you visited the emergency room?",
        type: "radio",
        section: "Healthcare Utilization",
        options: [
          { value: "none", label: "None", score: 0 },
          { value: "one", label: "1 time", score: 1 },
          { value: "two_three", label: "2–3 times", score: 2 },
          { value: "four_plus", label: "4 or more times", score: 3 }
        ]
      },
      {
        id: "hospital_stays",
        text: "In the past 6 months, have you been admitted to the hospital overnight?",
        type: "radio",
        section: "Healthcare Utilization",
        options: [
          { value: "no", label: "No", score: 0 },
          { value: "once", label: "Yes, once", score: 1 },
          { value: "multiple", label: "Yes, more than once", score: 3 }
        ]
      },
      {
        id: "pain",
        text: "In the past 4 weeks, how much has pain interfered with your normal activities?",
        type: "radio",
        section: "Pain & Function",
        options: [
          { value: "not_at_all", label: "Not at all", score: 0 },
          { value: "a_little", label: "A little bit", score: 1 },
          { value: "moderately", label: "Moderately", score: 2 },
          { value: "quite_a_bit", label: "Quite a bit", score: 3 },
          { value: "extremely", label: "Extremely", score: 4 }
        ]
      },
      {
        id: "mobility",
        text: "Do you have difficulty walking or climbing stairs?",
        type: "radio",
        section: "Pain & Function",
        options: [
          { value: "no", label: "No difficulty", score: 0 },
          { value: "some", label: "Some difficulty", score: 1 },
          { value: "much", label: "Much difficulty", score: 2 },
          { value: "unable", label: "Unable to do", score: 3 }
        ]
      },
      {
        id: "memory",
        text: "During the past month, have you had increasing difficulty with memory or thinking clearly?",
        type: "radio",
        section: "Cognitive Screening",
        options: [
          { value: "no", label: "No", score: 0 },
          { value: "sometimes", label: "Sometimes", score: 1 },
          { value: "often", label: "Often", score: 2 },
          { value: "always", label: "Always", score: 3 }
        ]
      },
      {
        id: "confusion",
        text: "Have family or friends expressed concern about your memory?",
        type: "radio",
        section: "Cognitive Screening",
        options: [
          { value: "no", label: "No", score: 0 },
          { value: "yes", label: "Yes", score: 2 }
        ]
      },
      {
        id: "social_isolation",
        text: "How often do you feel isolated from others?",
        type: "radio",
        section: "Social Support",
        options: [
          { value: "hardly_ever", label: "Hardly ever", score: 0 },
          { value: "some", label: "Some of the time", score: 1 },
          { value: "often", label: "Often", score: 2 }
        ]
      },
      {
        id: "caregiver",
        text: "Do you have someone who can help you if you become sick or disabled?",
        type: "radio",
        section: "Social Support",
        options: [
          { value: "yes_reliable", label: "Yes, reliable support", score: 0 },
          { value: "yes_limited", label: "Yes, but limited", score: 1 },
          { value: "no", label: "No", score: 3 }
        ]
      },
      {
        id: "advance_directives",
        text: "Do you have advance directives (living will or healthcare power of attorney)?",
        type: "radio",
        section: "Advance Directives",
        options: [
          { value: "yes_both", label: "Yes, both documents", score: 0 },
          { value: "yes_one", label: "Yes, one document", score: 0 },
          { value: "no_want", label: "No, but I would like help setting them up", score: 1 },
          { value: "no", label: "No, and I do not wish to discuss at this time", score: 0 }
        ]
      },
      {
        id: "tobacco",
        text: "Do you currently use tobacco products?",
        type: "radio",
        section: "Behavioral Health",
        options: [
          { value: "never", label: "Never used", score: 0 },
          { value: "former", label: "Former user", score: 0 },
          { value: "current", label: "Current user", score: 2 }
        ]
      },
      {
        id: "alcohol",
        text: "How many alcoholic drinks do you have in a typical week?",
        type: "radio",
        section: "Behavioral Health",
        options: [
          { value: "none", label: "None", score: 0 },
          { value: "1_7", label: "1–7 drinks", score: 0 },
          { value: "8_14", label: "8–14 drinks", score: 1 },
          { value: "15_plus", label: "15 or more drinks", score: 3 }
        ]
      },
      {
        id: "falls_history",
        text: "Have you fallen in the past 12 months?",
        type: "radio",
        section: "Safety",
        options: [
          { value: "no", label: "No", score: 0 },
          { value: "once", label: "Yes, once", score: 1 },
          { value: "multiple", label: "Yes, more than once", score: 3 }
        ]
      },
      {
        id: "medication_management",
        text: "Do you have difficulty managing or remembering to take your medications?",
        type: "radio",
        section: "Medication Safety",
        options: [
          { value: "no", label: "No difficulty", score: 0 },
          { value: "sometimes", label: "Sometimes", score: 1 },
          { value: "often", label: "Often", score: 2 }
        ]
      }
    ],
    maxScore: 38,
    interpretation: (score) => {
      if (score <= 5) return { level: "low", text: "Low health risk — continue routine care" };
      if (score <= 12) return { level: "medium", text: "Moderate health risk — targeted interventions recommended" };
      if (score <= 20) return { level: "high", text: "High health risk — comprehensive care management needed" };
      return { level: "critical", text: "Very high risk — intensive care coordination and immediate interventions needed" };
    }
  }
};

export default assessmentTemplates;
